import type { VolumeWeek } from '../../api/services/payroll.service';

// The arithmetic behind Weekly volume, kept apart from the page so it can
// be tested on its own. It reproduces the family sheet's columns: each
// week against the same week a year earlier.

const DAY = 86400000;

export const yearOf = (w: VolumeWeek) => new Date(w.weekEnding).getUTCFullYear();

export const yearsOf = (weeks: VolumeWeek[]): number[] =>
  Array.from(new Set(weeks.map(yearOf))).sort((a, b) => b - a);

export const weeksOfYear = (weeks: VolumeWeek[], year: number) =>
  weeks.filter((w) => yearOf(w) === year)
    .sort((a, b) => +new Date(a.weekEnding) - +new Date(b.weekEnding));

/**
 * The same week in another year: the week ending nearest the same calendar
 * date, within three days. By date rather than by "52 weeks back", which
 * drifts a day a year and would miss by 2018.
 */
export function sameWeekIn(weeks: VolumeWeek[], week: VolumeWeek, year: number): VolumeWeek | null {
  const d = new Date(week.weekEnding);
  const target = Date.UTC(year, d.getUTCMonth(), d.getUTCDate());
  let best: VolumeWeek | null = null;
  let bestGap = Infinity;
  for (const w of weeks) {
    const gap = Math.abs(+new Date(w.weekEnding) - target);
    if (gap <= 3 * DAY && gap < bestGap) { best = w; bestGap = gap; }
  }
  return best;
}

/** Percent change, "12.34" for +12.34%; null when there is nothing to compare. */
export function change(now: number | null | undefined, before: number | null | undefined): number | null {
  if (now == null || before == null || before === 0) return null;
  return Math.round(((now - before) / before) * 10000) / 100;
}

export const perStop = (w: Pick<VolumeWeek, 'revenue' | 'totalStops'>) =>
  (w.revenue != null && w.totalStops ? Math.round((w.revenue / w.totalStops) * 100) / 100 : null);

export const packagesPerStop = (w: Pick<VolumeWeek, 'totalPackages' | 'totalStops'>) =>
  (w.totalStops ? Math.round((w.totalPackages / w.totalStops) * 100) / 100 : null);

export const SUMMED = [
  'pickupPackages', 'pickupStops', 'deliveryPackages', 'deliveryStops',
  'ecommercePackages', 'ecommerceStops', 'totalPackages', 'totalStops', 'revenue',
] as const;
export type Summed = typeof SUMMED[number];

export function total(weeks: VolumeWeek[]): Record<Summed, number> {
  const out = Object.fromEntries(SUMMED.map((k) => [k, 0])) as Record<Summed, number>;
  for (const w of weeks) for (const k of SUMMED) out[k] += (w[k] as number | null) || 0;
  out.revenue = Math.round(out.revenue * 100) / 100;
  return out;
}

/**
 * Year to date against the same weeks of another year — only the weeks that have
 * a match, so a year with 40 weeks in is not compared with a whole one.
 */
export function yearToDate(weeks: VolumeWeek[], year: number, against: number) {
  const now = weeksOfYear(weeks, year);
  const pairs = now
    .map((w) => ({ w, was: sameWeekIn(weeks, w, against) }))
    .filter((p): p is { w: VolumeWeek; was: VolumeWeek } => p.was !== null);
  return {
    weeks: now.length,
    now: total(now),
    // Same-week sums, for a fair change.
    pairedNow: total(pairs.map((p) => p.w)),
    pairedWas: total(pairs.map((p) => p.was)),
    paired: pairs.length,
  };
}
